import apiClient from "./client.js";

/**
 * Fetch paginated incidents for the active workspace.
 * @param {{ cursor?: string, limit?: number, monitor_id?: string, status?: string, severity?: string, priority?: string, from?: string, to?: string }} [params]
 */
export async function fetchIncidents(params = {}) {
  const res = await apiClient.get("/incidents", { params });
  return res.data; // { data: [], meta: { next_cursor } }
}

/**
 * Fetch a single incident by ID (includes last 20 timeline events inline).
 * @param {string} id
 */
export async function fetchIncident(id) {
  const res = await apiClient.get(`/incidents/${id}`);
  return res.data.data;
}

/**
 * Download incidents matching the given filters as a CSV file.
 * @param {{ monitor_id?: string, status?: string, severity?: string, priority?: string, from?: string, to?: string }} [params]
 */
export async function exportIncidentsCsv(params = {}) {
  const res = await apiClient.get("/incidents/export", { params, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "incidents.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Create a manual incident.
 * @param {{ title: string, description?: string, severity?: string, priority?: string, monitor_id?: string }} body
 */
export async function createIncident(body) {
  const res = await apiClient.post("/incidents", body);
  return res.data.data;
}

/**
 * Update an incident's status, severity, priority, assignment, or add a note.
 * @param {string} id
 * @param {{ status?: string, severity?: string, priority?: string, assigned_to?: string, note?: string }} body
 */
export async function updateIncident(id, body) {
  const res = await apiClient.patch(`/incidents/${id}`, body);
  return res.data.data;
}

/**
 * Acknowledge an incident.
 * @param {string} id
 */
export async function acknowledgeIncident(id) {
  const res = await apiClient.post(`/incidents/${id}/acknowledge`);
  return res.data.data;
}

/**
 * Resolve an incident.
 * @param {string} id
 */
export async function resolveIncident(id) {
  const res = await apiClient.post(`/incidents/${id}/resolve`);
  return res.data.data;
}

/**
 * Unacknowledge an incident, returning it to open.
 * @param {string} id
 */
export async function unacknowledgeIncident(id) {
  const res = await apiClient.post(`/incidents/${id}/unacknowledge`);
  return res.data.data;
}

/**
 * Reopen a resolved incident.
 * @param {string} id
 */
export async function reopenIncident(id) {
  const res = await apiClient.post(`/incidents/${id}/reopen`);
  return res.data.data;
}

/**
 * Add a responder to an incident's command roster.
 * @param {string} id
 * @param {{ user_id: string, role?: string }} body
 */
export async function addIncidentResponder(id, body) {
  const res = await apiClient.post(`/incidents/${id}/responders`, body);
  return res.data.data;
}

/**
 * Remove a responder from an incident's command roster.
 * @param {string} id
 * @param {string} userId
 */
export async function removeIncidentResponder(id, userId) {
  const res = await apiClient.delete(`/incidents/${id}/responders/${userId}`);
  return res.data.data;
}

/**
 * Add an external, email-only stakeholder to an incident.
 * @param {string} id
 * @param {{ email: string, name?: string }} body
 */
export async function addIncidentStakeholder(id, body) {
  const res = await apiClient.post(`/incidents/${id}/stakeholders`, body);
  return res.data.data;
}

/**
 * Remove a stakeholder from an incident.
 * @param {string} id
 * @param {string} stakeholderId
 */
export async function removeIncidentStakeholder(id, stakeholderId) {
  const res = await apiClient.delete(`/incidents/${id}/stakeholders/${stakeholderId}`);
  return res.data.data;
}

/**
 * Email every current stakeholder on an incident a free-text update.
 * @param {string} id
 * @param {string} message
 */
export async function notifyIncidentStakeholders(id, message) {
  const res = await apiClient.post(`/incidents/${id}/stakeholders/notify`, { message });
  return res.data.data;
}

/**
 * Fetch paginated timeline events for an incident.
 * @param {string} id
 * @param {{ cursor?: string, limit?: number }} [params]
 */
export async function fetchIncidentTimeline(id, params = {}) {
  const res = await apiClient.get(`/incidents/${id}/timeline`, { params });
  return res.data; // { data: [], meta: { next_cursor } }
}

/**
 * Fetch incident analytics for the active workspace.
 * @param {{ from?: string, to?: string, monitor_id?: string }} [params]
 */
export async function fetchIncidentAnalytics(params = {}) {
  const res = await apiClient.get("/incidents/analytics", { params });
  return res.data.data;
}

export async function fetchIncidentDiagnosis(id) {
  const res = await apiClient.get(`/incidents/${id}/diagnosis`);
  return res.data.data;
}

export async function fetchIncidentSummary(id) {
  const res = await apiClient.get(`/incidents/${id}/summary`);
  return res.data.data;
}

export async function fetchIncidentCustomerUpdate(id) {
  const res = await apiClient.get(`/incidents/${id}/customer-update`);
  return res.data.data;
}

export async function fetchIncidentExplain(id) {
  const res = await apiClient.get(`/incidents/${id}/explain`);
  return res.data.data;
}

/**
 * Add an ad-hoc checklist item to an incident.
 * @param {string} id
 * @param {string} text
 */
export async function addChecklistItem(id, text) {
  const res = await apiClient.post(`/incidents/${id}/checklist`, { text });
  return res.data.data;
}

/**
 * Check/uncheck or edit a checklist item.
 * @param {string} id
 * @param {string} itemId
 * @param {{ text?: string, is_checked?: boolean }} body
 */
export async function updateChecklistItem(id, itemId, body) {
  const res = await apiClient.patch(`/incidents/${id}/checklist/${itemId}`, body);
  return res.data.data;
}

/**
 * Remove a checklist item.
 * @param {string} id
 * @param {string} itemId
 */
export async function removeChecklistItem(id, itemId) {
  const res = await apiClient.delete(`/incidents/${id}/checklist/${itemId}`);
  return res.data.data;
}

/**
 * Apply an IncidentTemplate's checklist to an incident (appends after any
 * existing items).
 * @param {string} id
 * @param {string} templateId
 */
export async function applyChecklistTemplate(id, templateId) {
  const res = await apiClient.post(`/incidents/${id}/checklist/apply-template`, {
    template_id: templateId,
  });
  return res.data.data;
}

/**
 * List incident templates for the active workspace.
 */
export async function fetchIncidentTemplates() {
  const res = await apiClient.get("/incident-templates");
  return res.data.data;
}

/**
 * Attach a bridge link (Slack/Zoom/Teams/etc) to an incident.
 * @param {string} id
 * @param {{ provider: string, bridge_url: string, label?: string }} body
 */
export async function startCollaboration(id, body) {
  const res = await apiClient.post(`/incidents/${id}/collaboration`, body);
  return res.data.data;
}

/**
 * Close a collaboration session.
 * @param {string} id
 * @param {string} sessionId
 */
export async function endCollaboration(id, sessionId) {
  const res = await apiClient.post(`/incidents/${id}/collaboration/${sessionId}/end`);
  return res.data.data;
}
